import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import useUndoActions from 'hooks/useUndoActions';
import api from 'api';
import useCallbackAsync from 'hooks/useCallbackAsync';
import { NDATemplate } from 'models/Nda';
import NdaOptions from '../NdaOptions';
import RefreshIcon from 'icons/Refresh.icon';
import { sortByName } from 'utils';

import './ArchivedNdasModal.styles.scss';

type ArchivedNdasModalProps = {
  className?: string;
  templates: NDATemplate[];
  isLoading: boolean;
  onOpenActiveModal: () => void;
  onAddActiveTemplate: (template: NDATemplate) => void;
  onAddArchivedTemplate: (template: NDATemplate) => void;
};

const ArchivedNdasModal: ForwardRefRenderFunction<
  OverlayRef,
  ArchivedNdasModalProps
> = (props, ref) => {
  const {
    className,
    isLoading,
    templates = [],
    onOpenActiveModal,
    onAddActiveTemplate,
    onAddArchivedTemplate,
  } = props;

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const [templateId, setTemplateId] = useState('');

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('archived-ndas-modal', className);

  const { t } = useTranslation();

  const sortedNdas = useMemo(() => sortByName(templates), [templates]);

  const {
    isUndoAvailable: isUndoAvailableRestore,
    setUndoTimeout: setUndoTimeoutRestore,
    clearUndoTimeout: clearUndoTimeoutRestore,
  } = useUndoActions();

  const clearUndoStates = useCallback(() => {
    clearUndoTimeoutRestore();
    setTemplateId('');
  }, [clearUndoTimeoutRestore]);

  const [onRestoreTemplate, isLoadingRestore] = useCallbackAsync(
    async (templateId: string) => {
      clearUndoStates();

      const { data } = await api.general.restoreNdaTemplate(templateId);

      setTemplateId(templateId);
      onAddActiveTemplate(data);
      setUndoTimeoutRestore();
    },
  );

  const onUndoRestore = useCallback(async () => {
    try {
      clearUndoStates();

      const { data } = await api.general.undoRestoreNdaTemplate(templateId);

      onAddArchivedTemplate(data);
    } catch {
      return;
    }
  }, [templateId, clearUndoStates, onAddArchivedTemplate]);

  const getActions = useCallback(
    (templateId: string) => (
      <Button
        variant="icon"
        icon={RefreshIcon}
        disabled={isLoadingRestore}
        onClick={() => onRestoreTemplate(templateId)}
      />
    ),
    [isLoadingRestore, onRestoreTemplate],
  );

  return (
    <>
      <Modal
        ref={modalRef}
        className={classes}
        title={t('acrhivedNdas')}
        lockScroll={false}
      >
        <NdaOptions
          isLoading={isLoading}
          templates={sortedNdas}
          getActions={getActions}
        >
          <Button
            variant="link"
            onClick={() => {
              clearUndoStates();
              onOpenActiveModal();
            }}
          >
            {t('seeActiveNdas')}
          </Button>
        </NdaOptions>

        <div className="actions">
          <Button variant="link" onClick={modal?.close}>
            {t('close')}
          </Button>
        </div>
      </Modal>
      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailableRestore}
        onUndo={onUndoRestore}
      >
        <p>{t('ndaRestored')}</p>
      </PopNotificationWithUndo>
    </>
  );
};

export default forwardRef(ArchivedNdasModal);
